import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { clientAPI } from "../api/client";
import { NumberOfPerPage } from "../utils/globalVariable";
import { formatDateHourDb } from "../utils/tools";
import {
  customDateFormatType,
  formatNumber,
} from "../utils/utils";

const NUMBER_NFT_PER_PAGE = NumberOfPerPage.brokerDebtDisbursementManagement;
const queryKey = "BrokerDebtDisbursement";

async function fetchBrokerDebtDisbursement(
  currentPage,
  search,
  employeeId,
  startTime,
  endTime
) {
  try {
    const options = {
      limit: NUMBER_NFT_PER_PAGE,
      page: currentPage,
    };

    if (search !== undefined) options.search = search;
    if (employeeId) options.employeeId = employeeId;
    if (startTime) options.startTime = customDateFormatType(startTime);
    if (endTime) options.endTime = customDateFormatType(endTime);

    const queryParams = new URLSearchParams(options).toString();
    let data = await clientAPI(
      "get",
      `/brokerDebtDisbursement/?${queryParams}`
    );
    const totalPages = Math.ceil(data?.pagination?.total / NUMBER_NFT_PER_PAGE);
    const entries = data?.pagination?.total;
    const startEntry =
      (data?.pagination?.page - 1) * data?.pagination?.limit + 1;
    const endEntry = Math.min(
      data?.pagination?.page * data?.pagination?.limit,
      entries
    );

    const newData = data?.items.map((item, index) => {
      return {
        id: item?._id || "",
        codeEmployee: item?.brokerDebtCertificate?.employee?.code || "-",
        nameEmployee: item?.brokerDebtCertificate?.employee?.fullName || "-",
        disbursedAmount: item?.disbursedAmount
          ? `${formatNumber(item?.disbursedAmount)} VNĐ`
          : "0 VNĐ",
        amountHeld: item?.brokerDebtCertificate?.amountHeld
          ? `${formatNumber(item?.brokerDebtCertificate?.amountHeld)} VNĐ`
          : "0 VNĐ",
        amountPaid: item?.brokerDebtCertificate?.amountPaid
          ? `${formatNumber(item?.brokerDebtCertificate?.amountPaid)} VNĐ`
          : "0 VNĐ",
        createdAt: item?.createdAt ? formatDateHourDb(item?.createdAt) : "-",
        employeeId: item?.brokerDebtCertificate?.employeeId || "",
        edit: {
          id: item?._id || "",
          brokerDebtCertificateId: item?.brokerDebtCertificateId || "",
          disbursedAmount: item?.disbursedAmount || 0,
        },
      };
    });

    return { data: newData, totalPages, entries, startEntry, endEntry };
  } catch (error) {
    console.error("Error fetching useBrokerDebtDisbursement data:", error);
    return { data: [], totalPages: 0 };
  }
}

export function useBrokerDebtDisbursement(
  search,
  employeeId,
  startDate,
  endDate
) {
  const [currentPage, setCurrentPage] = useState(1);

  const { data, refetch, isLoading, isRefetching } = useQuery({
    queryKey: [queryKey, currentPage, search, employeeId, startDate, endDate],
    queryFn: () =>
      fetchBrokerDebtDisbursement(
        currentPage,
        search,
        employeeId,
        startDate,
        endDate
      ),
    refetchOnWindowFocus: false,
    staleTime: 30000,
    keepPreviousData: true,
    onError: (error) => {
      console.error("Error fetching checkIn data:", error);
    },
  });

  const nextPage = () => {
    if (currentPage < data?.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return {
    brokerDebtDisbursementData: data?.data,
    totalPages: data?.totalPages,
    refetch,
    isLoading,
    isRefetching,
    prevPage,
    nextPage,
    setCurrentPage,
    currentPage,
    entries: data?.entries,
    startEntry: data?.startEntry,
    endEntry: data?.endEntry,
  };
}
